/**
 * Invoice Calculation Engine
 *
 * Evaluates template-driven formula rules for invoice totals and line-item
 * computed columns. Uses a sandboxed Function constructor so formulas stay
 * as plain strings in the database — no code deployment needed when tax
 * rules or client-specific calculations change.
 *
 * Supported formula syntax
 * ─────────────────────────
 *   • Basic arithmetic:  + - * / ( )  (native JS ** also works for powers)
 *   • Comparisons/logic: > < >= <= === !== && || ?: (plain JS — a formula
 *                        is any valid JS expression, not a restricted
 *                        mini-language, so e.g. a ternary conditional
 *                        already works with no special support needed)
 *   • Context variables: subtotal, discount, taxableAmount, gst, tds,
 *                        effectiveGstRate, tdsRate, headerDiscount, …
 *                        (calc rules) or another column's own `key`
 *                        (line-item computed columns)
 *   • Array aggregate:   sum(lineItems, 'fieldName')  — calc rules only,
 *                        there's no `lineItems` array in a per-row context
 *   • Math helpers:      abs(), round(), floor(), ceil(), min(), max()
 *   • Conditional:       iff(condition, valueIfTrue, valueIfFalse) — same
 *                        idea as Excel's IF(), for anyone more comfortable
 *                        with that than a ternary (named `iff`, not `if`,
 *                        since `if` is a reserved JS keyword and can't be
 *                        used as an identifier here)
 *
 * Examples
 * ─────────
 *   "sum(lineItems, 'amount')"
 *   "subtotal * effectiveGstRate / 100"
 *   "taxableAmount + gst - tds"
 *   "quantity * unitPrice * (1 - discount / 100)"
 *   "quantity > 10 ? unitPrice * 0.9 : unitPrice"
 *   "iff(quantity > 10, unitPrice * 0.9, unitPrice)"
 */

// ─── Safe math helpers exposed to formula context ────────────────────────────

const MATH_HELPERS = {
  sum: (arr, field) =>
    Array.isArray(arr)
      ? arr.reduce((s, row) => s + (parseFormulaNumber(row[field]) || 0), 0)
      : 0,
  abs: Math.abs,
  round: (v, decimals = 2) => {
    const d = Number.isFinite(decimals) ? decimals : 2
    const factor = 10 ** d
    return Math.round(Number(v) * factor) / factor
  },
  floor: Math.floor,
  ceil: Math.ceil,
  min: Math.min,
  max: Math.max,
  // Excel-style conditional (named `iff`, not `if` — `if` is a reserved JS
  // keyword and new Function() would throw a SyntaxError trying to declare
  // a parameter with that name). A plain JS ternary already works too
  // (formulas are arbitrary JS expressions) — this is purely a friendlier
  // alternative for anyone who thinks in Excel formulas.
  iff: (cond, ifTrue, ifFalse) => (cond ? ifTrue : ifFalse),
}

// Excel-style PascalCase helpers users often type in the formula bar
// (Round, Abs, …). Aliased onto the same implementations so
// `Round(unitPrice / 26, 2)` works the same as `round(unitPrice / 26, 2)`.
MATH_HELPERS.Round = MATH_HELPERS.round
MATH_HELPERS.Abs = MATH_HELPERS.abs
MATH_HELPERS.Floor = MATH_HELPERS.floor
MATH_HELPERS.Ceil = MATH_HELPERS.ceil
MATH_HELPERS.Min = MATH_HELPERS.min
MATH_HELPERS.Max = MATH_HELPERS.max
MATH_HELPERS.Iff = MATH_HELPERS.iff
MATH_HELPERS.Sum = MATH_HELPERS.sum

/**
 * Normalize common Excel-style helper names before eval / static checks so
 * the formula bar accepts any of Round/round, Abs/abs, etc.
 */
export function normalizeFormulaAliases(formula) {
  return String(formula || '').replace(
    /\b(Round|Abs|Floor|Ceil|Min|Max|Iff|Sum)\b/g,
    (name) => name.toLowerCase(),
  )
}

/**
 * Strip UI / Excel prefixes that sometimes get persisted into saved
 * templates (the line-item hint renders `ƒ {{ formula }}`; a copied or
 * API-stored value like `f rate * …` or `=rate * …` is a SyntaxError
 * inside the evaluator and used to zero every Auto field).
 */
export function sanitizeFormula(formula) {
  let s = String(formula == null ? '' : formula).trim()
  s = s.replace(/^[ƒf]\s+/, '')
  s = s.replace(/^=\s*/, '')
  return s.trim()
}

// Every math-helper name a formula string is allowed to call, exposed for
// UI-level validation (see findUnknownFormulaReferences below) without
// leaking the helpers' actual implementations.
export const FORMULA_HELPER_NAMES = Object.keys(MATH_HELPERS)

// Identifier-like tokens in a formula string, with quoted string literals
// stripped first (so e.g. the `'amount'` inside `sum(lineItems, 'amount')`
// isn't mistaken for a bare identifier reference) — shared by both
// findUnknownFormulaReferences and formulaReferencesKey below so there's
// exactly one place that knows what "a reference" looks like syntactically.
function extractFormulaIdentifiers(formula) {
  const withoutStringLiterals = String(formula || '').replace(/'[^']*'|"[^"]*"/g, '')
  return [...new Set(withoutStringLiterals.match(/[A-Za-z_$][A-Za-z0-9_$]*/g) || [])]
}

/**
 * Lightweight static check for a formula string: does it only reference
 * known names (the built-in math helpers plus whatever `validKeys` the
 * caller says are legitimate — e.g. every other column's `key` in the same
 * line-item table)? `safeEval()` itself never throws for an unknown
 * identifier — an undeclared reference just silently evaluates to 0 inside
 * its own try/catch — which is the right RUNTIME behavior (a bad formula
 * should never crash the invoice), but it means nothing ever tells whoever
 * authored the formula that they typo'd a column key; this is the
 * authoring-time counterpart used by InvoiceCreateManual.vue's inline
 * "Manage Columns" formula field to surface that as a real, visible error
 * instead.
 *
 * @param {string}   formula
 * @param {string[]} validKeys – e.g. every other column's `key`, or the
 *                               calc-rule context vars (subtotal, tdsRate…)
 * @returns {string[]} unique unrecognized identifiers (empty = OK)
 */
function isKnownFormulaIdentifier(id, known) {
  if (known.has(id)) return true
  const variants = aliasKeyVariants(id)
  for (let i = 0; i < variants.length; i += 1) {
    if (known.has(variants[i])) return true
  }
  const lower = String(id || '').toLowerCase()
  if (known.has(lower)) return true
  for (const k of known) {
    if (keysMatchAlias(id, k)) return true
  }
  return false
}

export function findUnknownFormulaReferences(formula, validKeys = []) {
  const normalized = normalizeFormulaAliases(sanitizeFormula(formula))
  const known = new Set([
    ...FORMULA_HELPER_NAMES,
    'true',
    'false',
    'null',
    'undefined',
    // Built-in conversion constants exposed by buildLineItemFormulaContext
    'daysInMonth',
    'DAYS_IN_MONTH',
  ])
  ;(validKeys || []).forEach((key) => {
    if (key == null || key === '') return
    known.add(key)
    aliasKeyVariants(key).forEach((alias) => known.add(alias))
  })
  return extractFormulaIdentifiers(normalized).filter((id) => !isKnownFormulaIdentifier(id, known))
}

/**
 * Does `formula` reference `key` as a bare identifier? Used to block
 * deleting/renaming a column that another column's formula still depends
 * on, so removing e.g. "Rate Card" doesn't silently leave a sibling
 * "Month to Day" formula referencing a column that no longer exists
 * (safeEval would just start returning 0 for it, with no visible warning).
 */
export function formulaReferencesKey(formula, key) {
  if (!key) return false
  return extractFormulaIdentifiers(normalizeFormulaAliases(sanitizeFormula(formula)))
    .some((id) => keysMatchAlias(id, key))
}

/**
 * Authoring-time parse check. `safeEval` never throws (bad syntax → 0),
 * which hid invalid formulas on production. Returns '' when the expression
 * tokenizes and parses; otherwise a short user-facing message.
 */
export function validateFormulaSyntax(formula) {
  const expr = normalizeFormulaAliases(sanitizeFormula(formula))
  if (!expr) return 'Enter a formula, or turn "Computed" back off for this column.'
  try {
    evaluateExpression(expr, buildEvalContext({}))
    return ''
  } catch (_) {
    return 'Invalid formula syntax — use + - * / ( ) and column keys (e.g. quantity * unitPrice).'
  }
}

// ─── Core evaluator ──────────────────────────────────────────────────────────

/** True when `name` is a legal JS identifier (safe as a Function parameter). */
function isValidJsIdentifier(name) {
  return typeof name === 'string' && /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(name)
}

/**
 * Normalize column `type` from UI / API / custom templates.
 * Custom templates sometimes persist "Computed" / whitespace variants that
 * would otherwise fail a strict `=== 'computed'` check and never evaluate.
 */
/** Prod templates / ATS catalog sometimes persist these instead of `computed`. */
const COMPUTED_TYPE_SYNONYMS = new Set([
  'computed', 'formula', 'formulae', 'calculated', 'calculation',
  'expression', 'calc', 'auto',
])

export function normalizeColumnType(type) {
  const t = String(type == null ? '' : type).trim().toLowerCase()
  if (COMPUTED_TYPE_SYNONYMS.has(t)) return 'computed'
  return t
}

/**
 * True when a line-item column should be formula-driven (Computed ON).
 * An explicit stored `type` always wins — leftover `computed: true` from a
 * prior toggle must not resurrect Auto after the user picked currency/text.
 * Blank type + a formula string is treated as computed (prod templates often
 * persist `formula` without `type: 'computed'`).
 */
export function isComputedColumn(col) {
  if (!col) return false
  const type = normalizeColumnType(col.type)
  if (type) return type === 'computed'
  if (col.computed === true || col.computed === 'true' || col.computed === 1) return true
  return !!sanitizeFormula(col.formula)
}

/**
 * Parse a user/API numeric value that may carry ₹, %, spaces, or Indian /
 * Western thousand separators (`2,33,430` / `2,233,430`). `Number("2,233,430")`
 * is NaN — that used to flow into `rate * …` as NaN and display as ₹0.00.
 * Returns null when the value is not numeric.
 */
export function parseFormulaNumber(value) {
  if (value === '' || value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'boolean') return value ? 1 : 0
  if (typeof value !== 'string') {
    if (typeof value.valueOf === 'function' && value.valueOf() !== value) {
      return parseFormulaNumber(value.valueOf())
    }
    return null
  }
  let s = value.trim()
  if (!s) return null
  s = s.replace(/[₹$€£%\s\u00a0]/g, '')
  if (!s) return null
  const negative = /^[-(]/.test(s)
  s = s.replace(/^[-(]+/, '').replace(/\)+$/, '')
  const lastComma = s.lastIndexOf(',')
  const lastDot = s.lastIndexOf('.')
  if (lastComma !== -1 && lastDot !== -1) {
    if (lastComma > lastDot) {
      s = s.replace(/\./g, '').replace(',', '.')
    } else {
      s = s.replace(/,/g, '')
    }
  } else if (lastComma !== -1) {
    if (/^\d{1,3}(,\d{2})+$/.test(s) || /^\d{1,3}(,\d{3})+$/.test(s)) {
      s = s.replace(/,/g, '')
    } else if (/^\d+,\d{1,2}$/.test(s)) {
      s = s.replace(',', '.')
    } else {
      s = s.replace(/,/g, '')
    }
  }
  if (!/^\d+(\.\d+)?([eE][+-]?\d+)?$/.test(s)) return null
  const n = Number(s)
  if (!Number.isFinite(n)) return null
  return negative ? -n : n
}

/**
 * Coerce blank / numeric-looking values so arithmetic formulas do not yield
 * NaN (which safeEval would then clamp to 0 and look "broken").
 */
function coerceFormulaValue(value) {
  if (value === '' || value === null || value === undefined) return 0
  if (typeof value === 'boolean') return value
  const n = parseFormulaNumber(value)
  if (n != null) return n
  return value
}

function aliasKeyVariants(key) {
  const raw = String(key || '')
  if (!raw) return []
  const lower = raw.toLowerCase()
  const snake = raw
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-.\s]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()
  const camel = snake.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase())
  const compact = lower.replace(/[^a-z0-9]/g, '')
  return [...new Set([raw, lower, snake, camel, compact].filter(Boolean))]
}

function keysMatchAlias(a, b) {
  if (a === b) return true
  const aSet = new Set(aliasKeyVariants(a).map((k) => k.toLowerCase()))
  return aliasKeyVariants(b).some((k) => aSet.has(k.toLowerCase()))
}

function readRowField(row, key) {
  if (!row || key == null) return undefined
  if (Object.prototype.hasOwnProperty.call(row, key) && row[key] !== undefined) {
    return row[key]
  }
  const keys = Object.keys(row)
  for (let i = 0; i < keys.length; i += 1) {
    if (keysMatchAlias(key, keys[i])) return row[keys[i]]
  }
  return undefined
}

function assignContextValue(ctx, key, value) {
  if (!key) return
  const coerced = coerceFormulaValue(value)
  if (isValidJsIdentifier(key) && !(key in ctx)) ctx[key] = coerced
  aliasKeyVariants(key).forEach((alias) => {
    if (isValidJsIdentifier(alias) && !(alias in ctx)) ctx[alias] = coerced
  })
}

/**
 * Build the variable map passed into line-item formulas: every column key
 * (seeded to 0 when missing), known conversion constants, and other safe
 * row fields. Also seeds camelCase / snake_case / lowercase aliases so a
 * production template that stores `day_to_month_ratio` still satisfies a
 * formula written as `dayToMonthRatio`.
 */
export function buildLineItemFormulaContext(row = {}, columns = []) {
  const ctx = {
    daysInMonth: DAYS_IN_MONTH,
    DAYS_IN_MONTH,
  }
  ;(columns || []).forEach((col) => {
    if (!col || !col.key) return
    const raw = readRowField(row, col.key)
    assignContextValue(ctx, col.key, raw === undefined ? 0 : raw)
  })
  Object.keys(row || {}).forEach((key) => {
    if (key in MATH_HELPERS || key in ctx) return
    assignContextValue(ctx, key, row[key])
  })
  return ctx
}

const EXPR_KEYWORDS = new Set(['true', 'false', 'null', 'undefined'])

function lookupContextValue(ctx, name) {
  if (name === 'true') return true
  if (name === 'false') return false
  if (name === 'null') return null
  if (name === 'undefined') return undefined
  if (Object.prototype.hasOwnProperty.call(ctx, name)) return ctx[name]
  const variants = aliasKeyVariants(name)
  for (let i = 0; i < variants.length; i += 1) {
    if (Object.prototype.hasOwnProperty.call(ctx, variants[i])) return ctx[variants[i]]
  }
  const lower = name.toLowerCase()
  const keys = Object.keys(ctx)
  for (let i = 0; i < keys.length; i += 1) {
    if (keys[i].toLowerCase() === lower) return ctx[keys[i]]
  }
  return 0
}

function tokenizeFormula(expr) {
  const tokens = []
  const s = String(expr || '')
  let i = 0
  while (i < s.length) {
    const ch = s[i]
    if (/\s/.test(ch)) {
      i += 1
      continue
    }
    if (ch === '"' || ch === "'") {
      const quote = ch
      let j = i + 1
      let str = ''
      while (j < s.length && s[j] !== quote) {
        if (s[j] === '\\' && j + 1 < s.length) {
          str += s[j + 1]
          j += 2
        } else {
          str += s[j]
          j += 1
        }
      }
      if (j >= s.length) throw new Error('Unterminated string')
      tokens.push({ type: 'STRING', value: str })
      i = j + 1
      continue
    }
    if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(s[i + 1] || ''))) {
      const m = s.slice(i).match(/^\d*(?:\.\d+)?(?:[eE][+-]?\d+)?/)
      if (!m || !m[0] || m[0] === '.') throw new Error('Invalid number')
      tokens.push({ type: 'NUMBER', value: parseFloat(m[0]) })
      i += m[0].length
      continue
    }
    if (/[A-Za-z_$]/.test(ch)) {
      const m = s.slice(i).match(/^[A-Za-z_$][A-Za-z0-9_$]*/)
      tokens.push({ type: 'IDENT', value: m[0] })
      i += m[0].length
      continue
    }
    const three = s.slice(i, i + 3)
    if (three === '===' || three === '!==') {
      tokens.push({ type: 'OP', value: three })
      i += 3
      continue
    }
    const two = s.slice(i, i + 2)
    if (['<=', '>=', '==', '!=', '&&', '||', '**'].includes(two)) {
      tokens.push({ type: 'OP', value: two })
      i += 2
      continue
    }
    if ('+-*/%()?:<>,!'.includes(ch)) {
      tokens.push({ type: 'OP', value: ch })
      i += 1
      continue
    }
    throw new Error(`Unexpected character: ${ch}`)
  }
  tokens.push({ type: 'EOF', value: '' })
  return tokens
}

function evaluateExpression(expr, ctx) {
  const tokens = tokenizeFormula(expr)
  let pos = 0
  const peek = () => tokens[pos]
  const consume = () => {
    const t = tokens[pos]
    pos += 1
    return t
  }
  const match = (value) => {
    if (peek().type === 'OP' && peek().value === value) {
      consume()
      return true
    }
    return false
  }

  function parseTernary() {
    let left = parseOr()
    if (match('?')) {
      const mid = parseTernary()
      if (!match(':')) throw new Error('Expected :')
      const right = parseTernary()
      left = left ? mid : right
    }
    return left
  }
  function parseOr() {
    let left = parseAnd()
    while (match('||')) left = left || parseAnd()
    return left
  }
  function parseAnd() {
    let left = parseEquality()
    while (match('&&')) left = left && parseEquality()
    return left
  }
  function parseEquality() {
    let left = parseCompare()
    for (;;) {
      if (match('===')) left = left === parseCompare()
      else if (match('!==')) left = left !== parseCompare()
      else if (match('==')) left = left == parseCompare() // eslint-disable-line eqeqeq
      else if (match('!=')) left = left != parseCompare() // eslint-disable-line eqeqeq
      else break
    }
    return left
  }
  function parseCompare() {
    let left = parseAdd()
    for (;;) {
      if (match('>=')) left = left >= parseAdd()
      else if (match('<=')) left = left <= parseAdd()
      else if (match('>')) left = left > parseAdd()
      else if (match('<')) left = left < parseAdd()
      else break
    }
    return left
  }
  function parseAdd() {
    let left = parseMul()
    for (;;) {
      if (match('+')) left = Number(left) + Number(parseMul())
      else if (match('-')) left = Number(left) - Number(parseMul())
      else break
    }
    return left
  }
  function parseMul() {
    let left = parseUnary()
    for (;;) {
      if (match('*')) left = Number(left) * Number(parseUnary())
      else if (match('/')) left = Number(left) / Number(parseUnary())
      else if (match('%')) left = Number(left) % Number(parseUnary())
      else break
    }
    return left
  }
  function parseUnary() {
    if (match('+')) return +parseUnary()
    if (match('-')) return -parseUnary()
    if (match('!')) return !parseUnary()
    return parsePower()
  }
  function parsePower() {
    const left = parsePrimary()
    if (match('**')) return left ** parseUnary()
    return left
  }
  function parsePrimary() {
    const t = peek()
    if (t.type === 'NUMBER') {
      consume()
      return t.value
    }
    if (t.type === 'STRING') {
      consume()
      return t.value
    }
    if (t.type === 'IDENT') {
      consume()
      const name = t.value
      if (match('(')) {
        const args = []
        if (!(peek().type === 'OP' && peek().value === ')')) {
          args.push(parseTernary())
          while (match(',')) args.push(parseTernary())
        }
        if (!match(')')) throw new Error('Expected )')
        const fn = lookupContextValue(ctx, name)
        if (typeof fn !== 'function') return 0
        return fn(...args)
      }
      return lookupContextValue(ctx, name)
    }
    if (match('(')) {
      const inner = parseTernary()
      if (!match(')')) throw new Error('Expected )')
      return inner
    }
    throw new Error('Unexpected token')
  }

  const result = parseTernary()
  if (peek().type !== 'EOF') throw new Error('Unexpected trailing input')
  return result
}

function finalizeNumericResult(result) {
  const n = typeof result === 'number' ? result : parseFormulaNumber(result)
  if (n == null || !Number.isFinite(n)) return 0
  return parseFloat(n.toFixed(6))
}

function buildEvalContext(context) {
  const ctx = { ...MATH_HELPERS }
  Object.keys(context || {}).forEach((key) => {
    if (key in MATH_HELPERS) return
    assignContextValue(ctx, key, context[key])
  })
  return ctx
}

/**
 * Fallback for exotic JS the parser does not cover. Uses a single context
 * object so a reserved-word row key (`default`, `class`, …) cannot make
 * `new Function(...keys)` throw and zero every computed column. Still
 * blocked by a CSP without `unsafe-eval` — the parser above is the
 * production-safe path.
 */
function evaluateWithFunction(expr, ctx) {
  const rewritten = tokenizeFormula(expr).slice(0, -1).map((t) => {
    if (t.type === 'IDENT' && !EXPR_KEYWORDS.has(t.value)) {
      return `__c[${JSON.stringify(t.value)}]`
    }
    if (t.type === 'STRING') return JSON.stringify(t.value)
    if (t.type === 'NUMBER') return String(t.value)
    return t.value
  }).join(' ')
  // eslint-disable-next-line no-new-func
  const fn = new Function('__c', `"use strict"; try { return (${rewritten}); } catch(_) { return 0; }`)
  return fn(ctx)
}

/**
 * Safely evaluate a formula string against a plain-object context.
 *
 * Prefer an eval-free parser so production CSP (no `unsafe-eval`) and
 * reserved-word / formatted-number edge cases cannot silently return 0
 * the way `new Function(...columnKeys)` did.
 *
 * @param {string} formula   – e.g. "quantity * unitPrice * (1 - discount / 100)"
 * @param {object} context   – variables available inside the formula
 * @returns {number}
 */
export function safeEval(formula, context = {}) {
  if (!formula || typeof formula !== 'string') return 0
  const expr = normalizeFormulaAliases(sanitizeFormula(formula))
  if (!expr) return 0
  const ctx = buildEvalContext(context)
  try {
    return finalizeNumericResult(evaluateExpression(expr, ctx))
  } catch (_) {
    try {
      return finalizeNumericResult(evaluateWithFunction(expr, ctx))
    } catch (__) {
      return 0
    }
  }
}

// ─── Headcount/staffing "Base Amount" auto-calc ──────────────────────────────
//
// Some invoice templates (RPO/staffing-style — see the reference invoice
// used to design this app's PDF layout) bill by "Total Head Count", "Rate
// Card" (a per-employee MONTHLY rate), "Month to Day Conversion" (that
// monthly rate converted to a per-day figure), and "Present Days" (person-
// days actually worked), rolling up to a "Base Amount". Those columns were
// previously just plain editable number/currency fields with no formula —
// i.e. "Base Amount" had to be typed in by hand, defeating the point of
// having the other columns at all.
//
// This block recognizes that column shape *by key/label pattern*, not by a
// specific named template, so it applies to ANY template that happens to
// carry these columns (built-in Line Items presets or per-invoice Manage Columns)
// without needing every such template to be individually marked up with an
// explicit `type: 'computed'` + formula string.
//
// Verified formula (reverse-engineered against a real reference invoice
// line so it's not a guess): for "ITI Electricians" — Head Count 10,
// Month-to-Day Conversion 1326, Present Days 199, Rate Card 39,790 — the
// reference Base Amount is 263,927.
//   • Month-to-Day Conversion × Present Days = 1326 × 199 = 263,874
//     → within 0.02% of the reference (the small remaining gap is just this
//       one sample invoice's own day-count/rounding quirk — not chased any
//       further here, see computeHeadcountBaseAmount below for exactly
//       which rounding this engine applies and why).
//   • By contrast, the "obvious" guess — Rate Card × Present Days ÷
//     Month-to-Day-Conversion × Head Count — gives ≈59,715, off by ~4.4x.
// So Head Count is deliberately NOT re-multiplied in below: "Present Days"
// already represents the total person-days billed for this row/designation
// (e.g. ~20 days × 10 people ≈ 199), and Month-to-Day Conversion is already
// a per-person-day rate, not a per-row rate that still needs scaling by
// headcount on top.
const MONTH_TO_DAY_PATTERNS = ['monthtodayconversion', 'monthtoday', 'dailyrate', 'perdayrate']
const PRESENT_DAYS_PATTERNS = ['presentdays', 'presentday', 'dayspresent', 'daysworked']
const RATE_CARD_PATTERNS = ['ratecard', 'monthlyrate', 'monthlysalary']
const HEADCOUNT_AMOUNT_PATTERNS = ['baseamount', 'lineamount', 'amount']
// Standard days-in-a-month divisor for man-month / day-rate conversion
// (e.g. Round(presentDays / 26, 2) or Round(unitPrice / 26, 2)).
export const DAYS_IN_MONTH = 26

function normalizeColKey(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

function findColumnKeyByPatterns(columns, patterns) {
  const col = (columns || []).find((c) => {
    const k = normalizeColKey(c && c.key)
    const l = normalizeColKey(c && c.label)
    return patterns.some((p) => (k && k.includes(p)) || (l && l.includes(p)))
  })
  return col ? col.key : null
}

/**
 * Detects whether `columns` describes a headcount/staffing row shape (see
 * above) and, if so, returns the actual column keys involved. Returns null
 * when the columns don't carry enough of this shape to compute anything —
 * callers should fall back to normal `type: 'computed'` formula handling in
 * that case (e.g. a plain quantity × rate template).
 *
 * @param {Array} columns – template.lineItemColumns (or the dynamic/merged
 *                           column config the wizard is actually rendering)
 */
export function findHeadcountCalcColumns(columns = []) {
  const amountKey = findColumnKeyByPatterns(columns, HEADCOUNT_AMOUNT_PATTERNS)
  const presentDaysKey = findColumnKeyByPatterns(columns, PRESENT_DAYS_PATTERNS)
  const monthToDayKey = findColumnKeyByPatterns(columns, MONTH_TO_DAY_PATTERNS)
  const rateCardKey = findColumnKeyByPatterns(columns, RATE_CARD_PATTERNS)
  if (!amountKey || !presentDaysKey || (!monthToDayKey && !rateCardKey)) return null
  return { amountKey, presentDaysKey, monthToDayKey, rateCardKey }
}

/**
 * Derives a headcount/staffing row's Base Amount from Rate Card / Month-to-
 * Day Conversion / Present Days (see findHeadcountCalcColumns doc above for
 * the verified formula) — returns null when this row/column-set isn't that
 * shape, so callers know to fall back to their normal handling instead.
 *
 * Rate Card, when the template has that column, is always the source of
 * truth for Month-to-Day Conversion (2026-07-22 fix): it's ROUNDED to the
 * nearest rupee and freshly re-derived from Rate Card on every call —
 * matching the reference invoice's own "Month to Day Conversion" column,
 * which is itself a rounded whole-rupee figure multiplied by Present Days,
 * not the raw fractional per-day rate (see the "verified formula" doc
 * comment above: 1326 × 199, not 1326.33 × 199) — and written back onto
 * `row[monthToDayKey]` so the UI can display it as a real number instead of
 * staying blank. Earlier revisions of this function only derived from Rate
 * Card when `row[monthToDayKey]` was blank, trusting any already-present
 * value (including one this same backfill had written on a PRIOR call)
 * verbatim — that meant Base Amount silently went stale/stuck the moment
 * Rate Card changed again after the first recompute (e.g. right after
 * addNewRow() seeds a row with Rate Card 0, Month-to-Day permanently
 * "locks" to 0 and never updates even once a real Rate Card is typed in).
 * Only templates with a Month-to-Day column but NO Rate Card column at all
 * (none of this app's built-ins today) still read `row[monthToDayKey]`
 * directly, as a genuinely manually-entered per-day rate.
 * `row` is always a fresh per-call copy (see recomputeRow below), so this
 * mutation never leaks into caller state.
 */
export function computeHeadcountBaseAmount(row, columns = []) {
  const found = findHeadcountCalcColumns(columns)
  if (!found) return null

  const presentDays = parseFormulaNumber(row[found.presentDaysKey])
  if (presentDays == null) return null

  let perDayRate = null
  if (found.rateCardKey) {
    const rateCard = parseFormulaNumber(row[found.rateCardKey])
    if (rateCard != null) {
      perDayRate = MATH_HELPERS.round(rateCard / DAYS_IN_MONTH, 2)
      if (found.monthToDayKey) row[found.monthToDayKey] = perDayRate
    }
  } else if (found.monthToDayKey) {
    const rawMonthToDay = row[found.monthToDayKey]
    if (rawMonthToDay !== undefined && rawMonthToDay !== null && rawMonthToDay !== '') {
      perDayRate = parseFormulaNumber(rawMonthToDay)
    }
  }
  if (!isFinite(perDayRate)) return null

  return parseFloat((perDayRate * presentDays).toFixed(2))
}

/**
 * Recompute all "computed" columns in a line item row and return a new row.
 *
 * @param {object}   row      – current row values
 * @param {Array}    columns  – template.lineItemColumns array (or the
 *                              live/dynamic column config the wizard is
 *                              actually rendering — see InvoiceCreateManual
 *                              .vue's lineItemValidationColumns, which is
 *                              what callers should pass so a column a user
 *                              just added/marked-computed via "Manage
 *                              Columns" recomputes too, not just columns
 *                              baked into the saved template)
 * @returns {object} updated row
 */
export function recomputeRow(row, columns = []) {
  const updated = { ...row }
  const colByKey = new Map((columns || []).filter((c) => c && c.key).map((c) => [c.key, c]))

  // Seed every column key so formulas always see identifiers (missing keys
  // used to ReferenceError inside safeEval and silently become 0).
  ;(columns || []).forEach((col) => {
    if (!col || !col.key) return
    if (updated[col.key] === undefined) updated[col.key] = 0
  })

  // Headcount engine (Rate Card ÷ 26 × Present Days) only applies when the
  // amount column is still Computed *and* has no user-authored formula.
  // Turning Computed off (manual baseAmount/amount) or setting e.g.
  // `Round(presentDays / 26, 2)` must not be overwritten by the template heuristic.
  const headcountKeys = findHeadcountCalcColumns(columns)
  if (headcountKeys) {
    const amountCol = colByKey.get(headcountKeys.amountKey)
    const amountIsComputed = isComputedColumn(amountCol)
    const amountHasUserFormula = !!(amountCol && String(amountCol.formula || '').trim())
    // Manual = explicitly not Computed (formula text may still be retained
    // so the user can toggle Computed back on without retyping).
    const amountIsManual = amountCol && !amountIsComputed
    if (!amountIsManual && !amountHasUserFormula) {
      const headcountAmount = computeHeadcountBaseAmount(updated, columns)
      if (headcountAmount != null) {
        updated[headcountKeys.amountKey] = headcountAmount
      }
    } else if (!amountIsManual && headcountKeys.rateCardKey && headcountKeys.monthToDayKey) {
      // Refresh Month-to-Day from Rate Card only when MTD itself is Computed
      // without a user formula. Manual currency/number MTD must keep the
      // typed value (formulas like monthToDayConversion * presentDays rely on it).
      const mtdCol = colByKey.get(headcountKeys.monthToDayKey)
      const mtdHasFormula = !!(mtdCol && String(mtdCol.formula || '').trim())
      if (mtdCol && isComputedColumn(mtdCol) && !mtdHasFormula) {
        const rateCard = parseFormulaNumber(updated[headcountKeys.rateCardKey])
        if (rateCard != null) {
          updated[headcountKeys.monthToDayKey] = MATH_HELPERS.round(rateCard / DAYS_IN_MONTH, 2)
        }
      }
    }
  }

  // Evaluate Computed columns that carry a formula. Multi-pass so dependent
  // formulas work regardless of column order (e.g. amount before quantity,
  // or baseAmount before a computed monthToDayConversion) — critical for
  // custom templates / drag-reordered Edit Columns layouts.
  const formulaCols = (columns || []).filter(
    (col) => col && col.key && isComputedColumn(col) && String(col.formula || '').trim(),
  )
  if (formulaCols.length) {
    const maxPasses = Math.max(formulaCols.length, 1)
    for (let pass = 0; pass < maxPasses; pass += 1) {
      let changed = false
      const ctx = buildLineItemFormulaContext(updated, columns)
      formulaCols.forEach((col) => {
        const next = safeEval(col.formula, ctx)
        if (updated[col.key] !== next) {
          updated[col.key] = next
          ctx[col.key] = next
          changed = true
        }
      })
      if (!changed) break
    }
  }
  return updated
}

// ─── Calculation context builder ─────────────────────────────────────────────

/**
 * Build the variable context passed to `evaluateCalcRules`.
 *
 * @param {Array}  lineItems  – current line item rows
 * @param {object} formData   – wizard formData (gstType, tdsApplicable, tdsPercentage, …)
 * @param {object} taxConfig  – template.taxConfig
 * @param {object} gstRates   – live rates from API (sgst, cgst, igst)
 * @returns {object}
 */
export function buildCalcContext(lineItems, formData, taxConfig = {}, gstRates = {}) {
  const gstType = formData.gstType || taxConfig.gstType || 'SGST-CGST'

  let effectiveGstRate = 0
  if (gstType === 'SEZ' || gstType === 'sez') {
    effectiveGstRate = 0
  } else if (gstType === 'IGST') {
    effectiveGstRate = parseFloat(gstRates.igst || taxConfig.igstRate || 18)
  } else {
    // SGST-CGST (default)
    effectiveGstRate =
      parseFloat(gstRates.sgst || taxConfig.sgstRate || 9) +
      parseFloat(gstRates.cgst || taxConfig.cgstRate || 9)
  }

  const tdsRate =
    formData.tdsApplicable && formData.tdsPercentage
      ? parseFloat(formData.tdsPercentage)
      : 0

  return {
    lineItems,
    effectiveGstRate,
    tdsRate,
    headerDiscount: parseFloat(formData.headerDiscount) || 0,
  }
}

// ─── Main totals evaluator ───────────────────────────────────────────────────

/**
 * Evaluate all calculation rules in order, accumulating results.
 * Each rule can reference variables produced by earlier rules in the list.
 *
 * @param {Array}  rules    – template.calculationRules
 * @param {object} context  – from buildCalcContext()
 * @returns {{ subtotal, discount, taxableAmount, gst, tds, total, … }}
 */
export function evaluateCalcRules(rules = [], context = {}) {
  const result = {}
  const running = { ...context }

  for (const rule of rules) {
    if (!rule || !rule.key) continue
    const value = safeEval(rule.formula || '0', running)
    result[rule.key] = value
    running[rule.key] = value
  }

  return result
}

// ─── Friendly accessors ──────────────────────────────────────────────────────

// ─── SAC / HSN Code (single field) ───────────────────────────────────────────
// Product rule: SAC and HSN are ONE field — one UI column and one API key.
// LUT (`lutNo` / `sattva_lut_no`) is a separate invoice-level field and must
// never be merged into SAC/HSN. Canonical storage key remains `sacCode`.

/** Display label used everywhere for the unified SAC/HSN field. */
export const SAC_HSN_LABEL = 'SAC / HSN Code'

/** Canonical key written to line items / header / API `lineItems[][sacCode]`. */
export const SAC_HSN_KEY = 'sacCode'

/** Multipart field for header SAC/HSN on POST /invoice. */
export const SAC_HSN_API_FIELD = 'sattva_sac_code'

/** Multipart field for header LUT on POST /invoice (separate from SAC/HSN). */
export const LUT_API_FIELD = 'sattva_lut_no'

/** Legacy SAC/HSN alias keys stripped from line-item rows before API payload. */
const SAC_HSN_ALIAS_KEYS = ['hsn', 'hsnCode', 'hsnLutCode', 'hsnSac']

/** LUT keys that must not live on line items — stripped during migration. */
const LINE_ITEM_LUT_KEYS = ['lut', 'lutCode', 'lutNo']

const SAC_HSN_KEY_ALIASES = new Set([
  'saccode', 'sac', 'hsn', 'hsncode', 'hsnsac',
  'hsnlut', 'hsnlutcode', // legacy merged column keys — read into sacCode only
])

/**
 * True when a column is (or was) a SAC / HSN code field (not LUT).
 */
export function isSacHsnColumn(col) {
  if (!col) return false
  if (col.type === 'sac-select') return true
  const k = String(col.key || '').toLowerCase().replace(/[^a-z0-9]/g, '')
  if (k === 'lut' || k === 'lutcode' || k === 'lutno') return false
  if (SAC_HSN_KEY_ALIASES.has(k)) return true
  const l = String(col.label || '').toLowerCase()
  if (/\blut\b/.test(l) && !/\b(hsn|sac)\b/.test(l)) return false
  return /\b(hsn|sac)\b/.test(l)
}

/**
 * Read SAC/HSN from an object that may use any historical SAC/HSN alias key.
 */
export function resolveSacHsnCode(obj) {
  if (!obj || typeof obj !== 'object') return ''
  const keys = ['sacCode', 'hsnLutCode', 'hsnSac', 'hsnCode', 'hsn']
  for (const k of keys) {
    const v = obj[k]
    if (v != null && String(v).trim() !== '') return v
  }
  return ''
}

/**
 * Read LUT number from header / company-details shapes (separate from SAC/HSN).
 */
export function resolveLutNo(obj) {
  if (!obj || typeof obj !== 'object') return ''
  const keys = ['lutNo', 'lutCode', 'lut']
  for (const k of keys) {
    const v = obj[k]
    if (v != null && String(v).trim() !== '') return v
  }
  return ''
}

/**
 * Collapse duplicate SAC/HSN columns into one `sacCode` column.
 * Safe for presets that already have a single column and for custom
 * templates / Manage-Columns drafts that accidentally kept both.
 * Preserves a user-renamed header from Edit Columns; only falls back to
 * SAC_HSN_LABEL when the kept column has no non-empty label.
 */
export function dedupeSacHsnColumns(columns = []) {
  if (!Array.isArray(columns) || !columns.length) return columns || []
  const out = []
  let kept = false
  for (const col of columns) {
    if (!isSacHsnColumn(col)) {
      out.push(col)
      continue
    }
    if (kept) continue
    kept = true
    const customLabel = String((col && col.label) || '').trim()
    out.push({
      ...col,
      key: SAC_HSN_KEY,
      label: customLabel || SAC_HSN_LABEL,
      type: 'sac-select',
    })
  }
  return out
}

/**
 * Copy any legacy SAC/HSN alias on a line-item row onto canonical `sacCode`.
 */
export function migrateLineItemSacHsn(item) {
  if (!item || typeof item !== 'object') return item
  const code = resolveSacHsnCode(item)
  if (code !== '') item[SAC_HSN_KEY] = code
  SAC_HSN_ALIAS_KEYS.forEach((k) => {
    if (k in item) delete item[k]
  })
  LINE_ITEM_LUT_KEYS.forEach((k) => {
    if (k in item) delete item[k]
  })
  return item
}

function buildLineItemExtra(item, sacCode) {
  return migrateLineItemSacHsn({ ...item, [SAC_HSN_KEY]: sacCode })
}

/** Built-in line-item titles for staffing invoices when master data has none. */
export const LINE_ITEM_TITLE_OPTIONS = Object.freeze([
  'Manpower Supply',
  'Recruitment Fees',
  'Contract Staffing',
  'Temporary Staffing',
  'Payroll Processing',
  'Background Verification',
  'Training Fees',
  'Consulting Fees',
  'Anti Fraud Services',
  'Miscellaneous',
])

/**
 * Multi-column staffing / RPO / Reliance / Placement keys. Presence of any
 * of these means the row is not the Standard "1 Line Items" shape
 * (Item Details + Quantity + Rate + Amount).
 */
const MULTI_COL_LINE_ITEM_KEYS = Object.freeze([
  'employeeid',
  'employeename',
  'totalheadcount',
  'ratecard',
  'presentdays',
  'candidatename',
  'ctc',
  'payoutrate',
  'daytomonthratio',
  'igstrate',
  'taxablevalue',
])

/**
 * True for the Standard / "1 Line Items" column set: Item Details (or
 * title) plus Quantity + Rate, without Headcount / RPO / Reliance /
 * Placement extra columns. DEFAULT_TEMPLATE (`id: 'standard'`) is this
 * shape; Headcount / Reliance / Placement / RPO are not.
 */
export function isOneLineItemsStructure(columns = []) {
  const keys = new Set(
    (columns || []).map((c) => String((c && c.key) || '').toLowerCase().replace(/[^a-z0-9]/g, '')),
  )
  if (!keys.has('itemname') && !keys.has('title')) return false
  const hasQtyRate = keys.has('quantity') && (keys.has('unitprice') || keys.has('rate'))
  if (!hasQtyRate) return false
  return !MULTI_COL_LINE_ITEM_KEYS.some((k) => keys.has(k))
}

/**
 * Title dropdown is only for the Standard / "1 Line Items" structure.
 * Other presets keep Item Details / Description as a plain text field.
 * Saved Standard templates still use type `text` on `itemName` — when
 * `columns` is the 1-line set, treat that key as a title select.
 */
export function isTitleSelectColumn(col, columns = []) {
  if (!col) return false
  const type = normalizeColumnType(col.type)
  // Honor an explicit stored type that is not title-select (user changed it).
  if (type && type !== 'title-select') return false
  const typeMatch = type === 'title-select'
  const key = String(col.key || '').toLowerCase().replace(/[^a-z0-9]/g, '')
  const isTitleKey = key === 'itemname' || key === 'title'
  if (!typeMatch && !isTitleKey) return false
  if (columns && columns.length) return isOneLineItemsStructure(columns)
  // No column list (e.g. Update Invoice 1-line editor): honor explicit type only.
  return typeMatch
}

function lineItemColumnKeySet(columns = []) {
  return new Set(
    (columns || []).map((c) => String((c && c.key) || '').toLowerCase().replace(/[^a-z0-9]/g, '')),
  )
}

/**
 * Placement (CTC) / OLD Template candidate table and column clones:
 * Candidate Name plus CTC / Payout Rate / JR / Band.
 */
export function isCandidateCtcLineItemsStructure(columns = []) {
  const keys = lineItemColumnKeySet(columns)
  if (!keys.has('candidatename')) return false
  return keys.has('ctc') || keys.has('payoutrate') || keys.has('jrnumber') || keys.has('band')
}

/**
 * Optional overlay title ("Add title") — Placement (CTC) candidate table,
 * Headcount / Contract Staffing, Reliance, and column clones.
 * Standard / "1 Line Items" already has the Item Details title-select.
 */
export function supportsOptionalLineItemTitle(columns = []) {
  if (!columns || !columns.length) return false
  if (isOneLineItemsStructure(columns)) return false
  if (isCandidateCtcLineItemsStructure(columns)) return true
  const keys = lineItemColumnKeySet(columns)
  if (keys.has('totalheadcount') || keys.has('ratecard')) return true
  if (keys.has('daytomonthratio') || (keys.has('igstrate') && keys.has('taxablevalue'))) return true
  return false
}

/** Explicit `title` only — never fall back to Candidate Name / Description. */
export function resolveOptionalOverlayTitle(item) {
  if (!item || typeof item !== 'object') return ''
  return String(item.title == null ? '' : item.title).trim()
}

function addUniqueTitle(out, seen, raw) {
  const value = String(raw == null ? '' : raw).trim()
  if (!value) return
  const key = value.toLowerCase()
  if (seen.has(key)) return
  seen.add(key)
  out.push(value)
}

function titleFromSacOption(opt) {
  if (opt == null) return ''
  if (typeof opt === 'string') {
    const parts = opt.split(' - ')
    return parts.length > 1 ? parts.slice(1).join(' - ').trim() : ''
  }
  if (opt.description) return String(opt.description).trim()
  const text = String(opt.text || opt.title || opt.label || '').trim()
  if (text.includes(' - ')) return text.split(' - ').slice(1).join(' - ').trim()
  return ''
}

/**
 * Merge built-in titles with SAC descriptions and values already on rows
 * so a custom typed title stays selectable after save/reload.
 */
export function buildLineItemTitleOptions({
  sacCodeOptions = [],
  lineItems = [],
  extra = [],
} = {}) {
  const seen = new Set()
  const out = []
  LINE_ITEM_TITLE_OPTIONS.forEach((t) => addUniqueTitle(out, seen, t))
  ;(extra || []).forEach((t) => addUniqueTitle(out, seen, t))
  ;(sacCodeOptions || []).forEach((opt) => addUniqueTitle(out, seen, titleFromSacOption(opt)))
  ;(lineItems || []).forEach((item) => {
    if (!item || typeof item !== 'object') return
    addUniqueTitle(out, seen, item.title)
    addUniqueTitle(out, seen, item.itemName)
  })
  return out
}

/** Prefer explicit `title`, then Item Details (`itemName`). */
export function resolveLineItemTitle(item) {
  if (!item || typeof item !== 'object') return ''
  const title = String(item.title == null ? '' : item.title).trim()
  if (title) return title
  return String(item.itemName == null ? '' : item.itemName).trim()
}

/**
 * Map a wizard line-item row to the legacy invoice API shape while preserving extras.
 */
export function normalizeLineItemForApi(item, { isRpo = false } = {}) {
  if (!item) return null
  const sacCode = resolveSacHsnCode(item)
  if (isRpo) {
    const amount = parseFloat(item.baseAmount || item.amount || 0)
    return {
      itemName: [item.employeeName, item.bu].filter(Boolean).join(' - '),
      sacCode,
      quantity: 1,
      unitPrice: amount,
      amount,
      extra: buildLineItemExtra(item, sacCode),
    }
  }
  const quantity = parseFloat(item.quantity) || 0
  const unitPrice = parseFloat(item.unitPrice ?? item.rate) || 0
  // BUG FIX (2026-07-22): non-RPO headcount-shaped templates (e.g. the
  // "Headcount Billing (Month-to-Day)" built-in — see HEADCOUNT_TEMPLATE)
  // have no `quantity`/`unitPrice` columns at all, so `item.amount` is
  // always undefined and this used to silently fall through to
  // `quantity * unitPrice` = 0 — the real total (`item.baseAmount`, set by
  // computeHeadcountBaseAmount/recomputeRow) was dropped from the flattened
  // legacy `amount` field entirely (though it did still survive in the
  // full-fidelity `extra`/line_items_json payload — see the caller). Falling
  // back to `item.baseAmount` here fixes the flattened field too, and is a
  // no-op for every existing template that already sets `item.amount`.
  const amount = parseFloat(item.amount) || parseFloat(item.baseAmount) || parseFloat((quantity * unitPrice).toFixed(2))
  const itemName = item.itemName || item.description || ''
  const title = String(item.title == null ? '' : item.title).trim()
  return {
    itemName: itemName || title,
    sacCode,
    quantity,
    unitPrice,
    amount,
    extra: buildLineItemExtra({ ...item, itemName: itemName || title, title }, sacCode),
  }
}

/**
 * Apply template tax/header defaults onto wizard formData (non-destructive).
 */
export function applyTemplateDefaults(formData, templateConfig = {}) {
  const tax = templateConfig.taxConfig || {}
  const next = { ...formData }
  if (tax.gstType && tax.gstType !== 'auto') {
    next.gstType = tax.gstType
  }
  if (tax.tdsApplicable !== undefined) {
    next.tdsApplicable = !!tax.tdsApplicable
  }
  if (tax.tdsDefaultRate != null) {
    next.tdsPercentage = tax.tdsDefaultRate
  }
  ;(templateConfig.headerFields || []).forEach((field) => {
    if (field.default !== undefined && field.key && next[field.key] == null) {
      next[field.key] = field.default
    }
  })
  return next
}

/**
 * Returns only the rules marked `visible: true`, ready to render in the
 * Amount Summary section of the wizard.
 *
 * (2026-07-22: this docblock used to sit above normalizeLineItemForApi
 * instead of here — a leftover from an earlier reordering of this file's
 * functions that never got its comment moved along with it — moved to
 * where it actually belongs.)
 *
 * @param {Array}  rules      – template.calculationRules
 * @param {object} calcResult – output of evaluateCalcRules()
 * @returns {Array<{ key, label, value, highlight }>}
 */
export function getVisibleCalcRows(rules = [], calcResult = {}) {
  return rules
    .filter((r) => r.visible !== false)
    .map((r) => ({
      key: r.key,
      label: r.label || r.key,
      value: parseFloat((calcResult[r.key] || 0).toFixed(2)),
      highlight: !!r.highlight,
    }))
}

// ─── Default template definitions ────────────────────────────────────────────

/**
 * Built-in fallback used when no API template is found for the client.
 * Matches the existing generic line-item layout.
 */
export const DEFAULT_TEMPLATE = Object.freeze({
  id: 'standard',
  // UI name is "Standard"; this is the "1 Line Items" structure
  // (Item Details + Qty + Rate + Amount). Optional title select lives here only.
  name: 'Standard',
  layout: {
    // 'bajaj' (Old Template): default preview skin for new invoices when no
    // style is set on the template or override. Legacy 'standard' still maps
    // to 'modern' below for pre-rename saved data.
    style: 'bajaj',
    showLogo: true,
    logoPosition: 'left',
    showBankDetails: true,
    showTerms: true,
    footerText: '',
  },
  headerFields: [
    { key: 'invoiceDate', label: 'Invoice Date', type: 'date', required: true, visible: true },
    { key: 'poNumber', label: 'PO/WO Number', type: 'text', required: false, visible: true },
    { key: 'billingMonth', label: 'Billing Month', type: 'monthpicker', required: false, visible: true },
    { key: 'description', label: 'Description', type: 'textarea', required: false, visible: true },
  ],
  lineItemColumns: [
    { key: 'move', label: '', type: 'move', width: '40px' },
    // Standard / "1 Line Items": optional title dropdown (not required).
    { key: 'itemName', label: 'Item Details', type: 'title-select', required: false },
    { key: 'sacCode', label: SAC_HSN_LABEL, type: 'sac-select', required: false },
    { key: 'quantity', label: 'Quantity', type: 'number', required: true },
    { key: 'unitPrice', label: 'Rate', type: 'currency', required: true },
    { key: 'amount', label: 'Amount', type: 'computed', formula: 'quantity * unitPrice' },
    { key: 'actions', label: '', type: 'actions', width: '90px' },
  ],
  taxConfig: {
    gstType: 'auto',
    sgstRate: 9,
    cgstRate: 9,
    igstRate: 18,
    tdsApplicable: false,
    tdsDefaultRate: 2,
  },
  calculationRules: [
    { key: 'subtotal', label: 'Sub Total', formula: "sum(lineItems, 'amount')", visible: true },
    { key: 'discount', label: 'Discount', formula: 'subtotal * headerDiscount / 100', visible: false },
    { key: 'taxableAmount', label: 'Taxable Amount', formula: 'subtotal - discount', visible: false },
    { key: 'gst', label: 'GST', formula: 'taxableAmount * effectiveGstRate / 100', visible: true },
    { key: 'tds', label: 'TDS Deductible', formula: 'taxableAmount * tdsRate / 100', visible: true },
    { key: 'total', label: 'Total Payable', formula: 'taxableAmount + gst - tds', visible: true, highlight: true },
  ],
})

/**
 * Built-in RPO template fallback — used when client name pattern matches
 * the existing RPO heuristic and no API template is found.
 */
export const RPO_TEMPLATE = Object.freeze({
  id: 'rpo',
  name: 'RPO',
  layout: DEFAULT_TEMPLATE.layout,
  headerFields: DEFAULT_TEMPLATE.headerFields,
  lineItemColumns: [
    { key: 'move', label: '', type: 'move', width: '40px' },
    { key: 'employeeId', label: 'Employee ID', type: 'text', required: false },
    { key: 'employeeName', label: 'Employee Name', type: 'text', required: true },
    { key: 'doj', label: 'DOJ', type: 'date', required: false },
    { key: 'bu', label: 'BU', type: 'text', required: false },
    { key: 'geo', label: 'GEO', type: 'text', required: false },
    { key: 'baseAmount', label: 'Base Amount', type: 'currency', required: true },
    { key: 'actions', label: '', type: 'actions', width: '90px' },
  ],
  taxConfig: DEFAULT_TEMPLATE.taxConfig,
  calculationRules: [
    { key: 'subtotal', label: 'Sub Total', formula: "sum(lineItems, 'baseAmount')", visible: true },
    { key: 'discount', label: 'Discount', formula: 'subtotal * headerDiscount / 100', visible: false },
    { key: 'taxableAmount', label: 'Taxable Amount', formula: 'subtotal - discount', visible: false },
    { key: 'gst', label: 'GST', formula: 'taxableAmount * effectiveGstRate / 100', visible: true },
    { key: 'tds', label: 'TDS Deductible', formula: 'taxableAmount * tdsRate / 100', visible: true },
    { key: 'total', label: 'Total Payable', formula: 'taxableAmount + gst - tds', visible: true, highlight: true },
  ],
})

/**
 * Built-in "Headcount Billing (Month-to-Day)" template — reproduces the
 * Sr.No / Description / Total Head Count / Rate Card / Month to Day
 * conversion / Present Days / Base Amount column layout from the reference
 * staffing-invoice screenshot this was built against (2026-07-22). Unlike
 * RPO_TEMPLATE (which carries its own dedicated Employee ID/Name/DOJ/BU/GEO
 * columns), this one is the plainer "just the headcount rate-card math"
 * shape: Base Amount is entirely driven by findHeadcountCalcColumns/
 * computeHeadcountBaseAmount above via the Rate Card + Present Days columns
 * (Month to Day Conversion auto-derives + backfills from Rate Card unless
 * typed in directly) — no template-specific formula wiring needed here at
 * all, the generic pattern-matching in this file already covers it.
 * `layout.showBillingPeriodNote` is intentionally off here: selecting
 * Headcount as Line items must not auto-insert the "* Billing Invoice for
 * <start> to <end> Month *" row above Doc Type (especially when Style is
 * Old Template / Bajaj). Contract Staffing still opts in via its own
 * layout flag when that visual skin needs the note.
 */
export const HEADCOUNT_TEMPLATE = Object.freeze({
  id: 'headcount',
  name: 'Headcount',
  layout: {
    ...DEFAULT_TEMPLATE.layout,
    showBillingPeriodNote: false,
  },
  headerFields: DEFAULT_TEMPLATE.headerFields,
  lineItemColumns: [
    { key: 'move', label: '', type: 'move', width: '40px' },
    { key: 'srNo', label: 'Sr.No', type: 'text', required: false },
    { key: 'itemName', label: 'Description', type: 'text', required: true },
    { key: 'totalHeadCount', label: 'Total Head Count', type: 'number', required: false },
    { key: 'rateCard', label: 'Rate Card', type: 'currency', required: true },
    { key: 'monthToDayConversion', label: 'Month to Day conversion', type: 'computed', required: false },
    { key: 'presentDays', label: 'Present Days', type: 'number', required: false },
    { key: 'baseAmount', label: 'Base Amount', type: 'computed', required: false },
    { key: 'actions', label: '', type: 'actions', width: '90px' },
  ],
  taxConfig: DEFAULT_TEMPLATE.taxConfig,
  calculationRules: [
    { key: 'subtotal', label: 'Sub Total', formula: "sum(lineItems, 'baseAmount')", visible: true },
    { key: 'discount', label: 'Discount', formula: 'subtotal * headerDiscount / 100', visible: false },
    { key: 'taxableAmount', label: 'Taxable Amount', formula: 'subtotal - discount', visible: false },
    { key: 'gst', label: 'GST', formula: 'taxableAmount * effectiveGstRate / 100', visible: true },
    { key: 'tds', label: 'TDS Deductible', formula: 'taxableAmount * tdsRate / 100', visible: true },
    { key: 'total', label: 'Total Payable', formula: 'taxableAmount + gst - tds', visible: true, highlight: true },
  ],
})

/**
 * Built-in "Contract Staffing" template (2026-07-23) — reproduces a
 * reference navy/teal staffing-invoice design (see the matching
 * `.invoice-style-contract-staffing` design system in
 * GenericInvoicePreview.vue for the full visual rationale). Reuses
 * HEADCOUNT_TEMPLATE's exact column set (the reference invoice this was
 * built against uses the identical Sr.No/Description/Total Head Count/
 * Rate Card/Month to Day conversion/Present Days/Base Amount layout — see
 * that template's own comment for the Month-to-Day/Base Amount formula
 * details) since it's the same headcount rate-card math, just wrapped in
 * a different visual skin via `layout.style`. Only `layout` differs from
 * HEADCOUNT_TEMPLATE — columns/taxConfig/calculationRules are identical,
 * so this is a pure "new paint on an existing proven shape" built-in
 * rather than a second parallel calculation path to maintain.
 */
export const CONTRACT_STAFFING_TEMPLATE = Object.freeze({
  id: 'contract-staffing',
  name: 'Contract Staffing',
  layout: {
    ...DEFAULT_TEMPLATE.layout,
    style: 'contract-staffing',
    showBillingPeriodNote: true,
  },
  headerFields: DEFAULT_TEMPLATE.headerFields,
  lineItemColumns: HEADCOUNT_TEMPLATE.lineItemColumns,
  taxConfig: DEFAULT_TEMPLATE.taxConfig,
  calculationRules: HEADCOUNT_TEMPLATE.calculationRules,
})

/**
 * Built-in "OLD Template" (2026-07-23) — the team's legacy placement /
 * recruitment-fee layout (SH1693 / Bajaj-style). Display name is "OLD
 * Template"; internal id/style key stay `bajaj` for localStorage compat.
 * Columns: Candidate Name · DOJ · Expense month · JR Number · Band · CTC ·
 * Payout Rate · Base Amount (= CTC × Payout Rate / 100). Visual skin is
 * `.invoice-style-bajaj` in GenericInvoicePreview.vue — double-border sheet,
 * soft yellow (#FFF9C4) highlight cells, grey table header (pdf2htmlEX SH1693).
 */
export const BAJAJ_TEMPLATE = Object.freeze({
  id: 'bajaj',
  // Line-items column preset name (Candidate / CTC / Payout Rate). Visual
  // skin stays independently selectable as "Old Template" in the Style picker.
  name: 'Placement (CTC)',
  layout: {
    ...DEFAULT_TEMPLATE.layout,
    style: 'bajaj',
    // Reference SH1693 uses black type + soft yellow (#FFF9C4) highlight
    // cells — not the blue accent the earlier preview skin invented.
    primaryColor: '#000000',
    showBillingPeriodNote: false,
    footerText: '',
  },
  headerFields: DEFAULT_TEMPLATE.headerFields,
  lineItemColumns: [
    { key: 'move', label: '', type: 'move', width: '40px' },
    { key: 'candidateName', label: 'Candidate Name', type: 'text', required: true },
    { key: 'doj', label: 'DOJ', type: 'date', required: false },
    { key: 'expenseMonth', label: 'Expense month', type: 'text', required: false },
    { key: 'jrNumber', label: 'JR Number', type: 'text', required: false },
    { key: 'band', label: 'Band', type: 'text', required: false },
    { key: 'ctc', label: 'CTC', type: 'currency', required: true },
    { key: 'payoutRate', label: 'Payout Rate', type: 'percentage', required: true },
    { key: 'baseAmount', label: 'Base Amount', type: 'computed', formula: 'ctc * payoutRate / 100' },
    { key: 'actions', label: '', type: 'actions', width: '90px' },
  ],
  taxConfig: DEFAULT_TEMPLATE.taxConfig,
  calculationRules: [
    { key: 'subtotal', label: 'Sub Total', formula: "sum(lineItems, 'baseAmount')", visible: true },
    { key: 'discount', label: 'Discount', formula: 'subtotal * headerDiscount / 100', visible: false },
    { key: 'taxableAmount', label: 'Taxable Amount', formula: 'subtotal - discount', visible: false },
    { key: 'gst', label: 'GST', formula: 'taxableAmount * effectiveGstRate / 100', visible: true },
    { key: 'tds', label: 'TDS Deductible', formula: 'taxableAmount * tdsRate / 100', visible: true },
    { key: 'total', label: 'Total Payable', formula: 'taxableAmount + gst - tds', visible: true, highlight: true },
  ],
})

/**
 * Built-in "Reliance" template (2026-07-23) — formal GST tax-invoice layout
 * reverse-engineered from Reliance Tax Invoice SH1690 (Jun 2026 pdf2htmlEX
 * sheet). Line columns match that form's thead (incl. Taxable value / IGST
 * Rate / IGST Amount / Total Invoice Amount). Visual skin is the
 * `.invoice-style-reliance` bordered-form rebuild in GenericInvoicePreview.vue
 * (light-blue #D9E9F3 header fills + 1px black grid; Total row on the
 * line-items grid; Common Seal column; reverse charge in the sign cell).
 */
export const RELIANCE_TEMPLATE = Object.freeze({
  id: 'reliance',
  name: 'Reliance',
  layout: {
    ...DEFAULT_TEMPLATE.layout,
    style: 'reliance',
    primaryColor: '#000000',
    showBillingPeriodNote: false,
    footerText: '',
    termsText: '',
  },
  headerFields: DEFAULT_TEMPLATE.headerFields,
  lineItemColumns: [
    { key: 'move', label: '', type: 'move', width: '40px' },
    { key: 'srNo', label: 'Sr No', type: 'text', required: false },
    { key: 'itemName', label: 'Description of Goods', type: 'text', required: true },
    { key: 'sacCode', label: SAC_HSN_LABEL, type: 'sac-select', required: false },
    { key: 'presentDays', label: 'Present Day', type: 'number', required: false },
    { key: 'totalBillableDays', label: 'Total Billable day', type: 'number', required: false },
    { key: 'rate', label: 'Rate', type: 'currency', required: true },
    { key: 'dayToMonthRatio', label: 'Day to Month Ratio', type: 'number', required: false },
    { key: 'baseAmount', label: 'Base Amount', type: 'computed', formula: 'rate * (dayToMonthRatio || 1)' },
    { key: 'taxableValue', label: 'Taxable value', type: 'computed', formula: 'baseAmount' },
    { key: 'igstRate', label: 'IGST Rate', type: 'percentage', required: false },
    { key: 'igstAmount', label: 'IGST Amount', type: 'computed', formula: 'taxableValue * (igstRate || 0) / 100' },
    { key: 'lineTotal', label: 'Total Invoice Amount', type: 'computed', formula: 'taxableValue + igstAmount' },
    { key: 'actions', label: '', type: 'actions', width: '90px' },
  ],
  taxConfig: DEFAULT_TEMPLATE.taxConfig,
  calculationRules: [
    { key: 'subtotal', label: 'Sub Total', formula: "sum(lineItems, 'taxableValue')", visible: true },
    { key: 'discount', label: 'Discount', formula: 'subtotal * headerDiscount / 100', visible: false },
    { key: 'taxableAmount', label: 'Taxable Amount', formula: 'subtotal - discount', visible: false },
    { key: 'gst', label: 'GST', formula: "sum(lineItems, 'igstAmount')", visible: true },
    { key: 'tds', label: 'TDS Deductible', formula: 'taxableAmount * tdsRate / 100', visible: true },
    { key: 'total', label: 'Total Payable', formula: "sum(lineItems, 'lineTotal') - tds", visible: true, highlight: true },
  ],
})

/**
 * Built-in Line Items column presets for the create-invoice wizard.
 * Shown in the Live Preview toolbar as "Line items" (next to Style).
 * Styles (Modern / Reliance / Bajaj / etc.) are chosen separately in the
 * preview Style picker — these entries only encode columns + calc rules.
 * When a style id matches a preset id (reliance / bajaj / contract-staffing),
 * InvoiceCreateManual syncs Style → Line items (manual override still allowed).
 */
export const LINE_ITEM_PRESETS = Object.freeze([
  DEFAULT_TEMPLATE,
  RPO_TEMPLATE,
  HEADCOUNT_TEMPLATE,
  CONTRACT_STAFFING_TEMPLATE,
  BAJAJ_TEMPLATE,
  RELIANCE_TEMPLATE,
])

/**
 * @deprecated Never persist Line Items preset choice in the browser.
 * Preset / style selection is stored on the invoice draft API
 * (`line_item_preset_id`, `layout_style`) and on invoice create.
 * Kept as a constant only so any stray import does not invent a new key.
 */
export const LAST_LINE_ITEMS_PRESET_KEY = 'crm_invoice_last_line_items_preset'
